import React, { useState } from 'react'
import { useAuth } from 'app/provider/auth'
import { GroupsFeedScreen } from './groups-feed-screen'
import { userFeedQuery, useUserFeedQuery } from './queries'
import { GroupPostCreationWizard } from '../../containers'

import { mergeArraysAtIntervals } from 'app/lib/merge-arrays-at-intervals'
import { GroupAd } from 'app/features/ads/blocks'
import { GroupAnnouncement, GroupEvent, GroupPost } from 'app/types'

type GroupsFeedScreenContainerProps = {
  HeaderComponent?: React.ReactNode
}

export type GroupPostOrAd =
  | { type: 'groupPost'; data: GroupPost }
  | { type: 'ad'; data: GroupAd }
  | { type: 'groupEvent'; data: GroupEvent }
  | { type: 'groupAnnouncement'; data: GroupAnnouncement }

export function GroupsFeedScreenContainer({ HeaderComponent }: GroupsFeedScreenContainerProps) {
  const { user } = useAuth()
  const [feedRefreshing, setFeedRefreshing] = useState(false)
  const [createPostModalOpen, setCreatePostModalOpen] = useState(false)

  const {
    data: feedData,
    networkStatus,
    refetch: refetchFeed,
    loading: feedLoading,
    fetchMore,
  } = useUserFeedQuery({
    variables: {
      ...userFeedQuery.defaultVariables,
    },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  })

  const feed = feedData?.getUserFeed

  const handlePullToRefresh = async () => {
    setFeedRefreshing(true)
    await refetchFeed()
    setFeedRefreshing(false)
  }

  const formattedFeed = userFeedQuery.formatFn(feed).map((item) => {
    if (item.type === 'groupAnnouncement') {
      return {
        type: 'groupAnnouncement' as const,
        data: item,
      }
    }

    if (item.type === 'groupEvent')
      return {
        type: 'groupEvent' as const,
        data: item,
      }

    return {
      type: 'groupPost' as const,
      data: item,
    }
  })

  const formattedAds =
    feed?.ads.map((ad) => ({
      type: 'ad' as const,
      data: ad,
    })) || []

  const feedWithAds = mergeArraysAtIntervals<GroupPostOrAd>(formattedFeed, formattedAds, 5, 5)

  const hasMore = (feedData?.getUserFeed?.totalDocs || 0) > formattedFeed.length

  const handleFetchMore = () => {
    if (!hasMore || !feed) return
    fetchMore({
      variables: {
        page: feed.page + 1,
        limit: 10,
      },
    })
  }

  const feedFetchingMore = networkStatus === 3
  const feedFetching = networkStatus === 2

  return (
    <>
      <GroupsFeedScreen
        onCreatePostOpenChange={setCreatePostModalOpen}
        user={user}
        HeaderComponent={HeaderComponent}
        feed={feedWithAds}
        feedLoading={feedLoading}
        feedFetching={feedFetching}
        feedFetchingMore={feedFetchingMore}
        fetchMore={handleFetchMore}
        hasMore={hasMore}
        refreshing={feedRefreshing}
        onPullToRefresh={handlePullToRefresh}
        refetchFeed={refetchFeed}
      />
      {user && (
        <GroupPostCreationWizard
          user={user}
          open={createPostModalOpen}
          onOpenChange={setCreatePostModalOpen}
          defaultGroup={null}
        />
      )}
    </>
  )
}
